<template>
    <div class="c-header-nav" ref="headerNav">
        <div class="c-header-nav__inner" :style="`max-height:${maxHeight}px`">
            <div class="c-header-nav__inner__time">
                <app-header-time custom-class="--mobile" />
            </div>
            <app-header-nav-links class="c-header-nav__inner__links" />

            <div class="c-header-nav__inner__more">
                <app-header-nav-more />
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

import gsap from "gsap";

import AppHeaderNavLinks from "components/AppHeader/AppHeaderNav/AppHeaderNavLinks/AppHeaderNavLinks";
import AppHeaderNavMore from "components/AppHeader/AppHeaderNav/AppHeaderNavMore/AppHeaderNavMore";
import AppHeaderTime from "components/AppHeader/AppHeaderTime";
export default {
    components: {
        AppHeaderNavLinks,
        AppHeaderNavMore,
        AppHeaderTime,
    },
    computed: {
        ...mapState({
            isNavOpen: (state) => state.navigation.isNavOpen,
        }),
    },
    data() {
        return {
            navTimeline: null,
            maxHeight: 0,
        };
    },

    created() {
        window.addEventListener("resize", this.onResize);
    },

    beforeDestroy() {
        this.navTimeline.kill();
    },
    destroyed() {
        window.removeEventListener("resize", this.onResize);
    },
    mounted() {
        this.onResize();
        this.createTimeline();
    },
    watch: {
        isNavOpen(bool) {
            this.toggleTimeLine(bool);
        },
        $route() {
            // close the nav during the page transition animation
            this.cleanUpTheTimeline();
        },
    },

    methods: {
        ////////////////////////////////
        //       START COPY ANIMATION ON CLOSE/OPEN
        ////////////////////////////////

        createTimeline() {
            this.navTimeline = gsap.timeline({});

            this.navTimeline.to(this.$refs.headerNav, {
                x: "0vw",
                y: "0vh",
                duration: 1,
                ease: "power2.inOut",
            });

            this.navTimeline.to(".c-header-nav .c-header-nav-link-item__router__span", {
                x: 0,
                y: 0,
                alpha: 1,
                stagger: 0.1,
            });
            this.navTimeline.to(".c-header-nav__inner__more", {
                y: 0,
                alpha: 1,
            });
            this.navTimeline.to(
                ".c-app-header-time--mobile",
                {
                    y: 0,
                    alpha: 1,
                },
                1
            );

            this.navTimeline.pause();
        },

        toggleTimeLine(bool) {
            // udpate the height of the viewport (to havoing the nav to be cropped)
            this.overwriteHeight();

            // pla
            bool && this.navTimeline ? this.navTimeline.play() : this.navTimeline.reverse();
        },

        ////////////////////////////////
        //       END COPY ANIMATION ON CLOSE/OPEN
        ////////////////////////////////

        ////////////////////////////////
        //       START ON RESIZE
        ////////////////////////////////
        onResize() {
            this.overwriteHeight();
            this.navTimeline ? this.restartTheNav() : null;
        },
        restartTheNav() {
            // could be improve. Restart the nav on resize to avoid bugs during resize

            //   reset the timeline
            this.cleanUpTheTimeline();

            // destroy and rebuild the timeline
            this.destroyAndRebuildTheTimeline();
        },
        cleanUpTheTimeline() {
            this.navTimeline.reverse();
            this.navTimeline.seek(0);
            //   make sure the props are remove
            this.clearProps();
        },
        clearProps() {
            gsap.set(this.$refs.headerNav, { clearProps: "all" });
        },
        destroyAndRebuildTheTimeline() {
            this.navTimeline.kill();
            this.navTimeline = null;
            this.isNavOpen ? this.$store.commit("navigation/TOGGLE_NAV", false) : null;

            this.createTimeline();
        },
        ////////////////////////////////
        //       END ON RESIZE
        ////////////////////////////////

        ////////////////////////////////
        //       START OVERWRITE THE HEIGHT
        ////////////////////////////////
        overwriteHeight() {
            // avoid the bottom of the nav to be hidden below the browser nav (on mobile)
            this.maxHeight = window.innerHeight;
        },
        ////////////////////////////////
        //       END OVERWRITE THE HEIGHT
        ////////////////////////////////
    },
};
</script>

<style lang="scss">
.c-header-nav {
    @include transform(translate(0vw, -100vh));
    position: fixed;
    width: 100vw;
    height: 100vh;
    background-color: $color-dark;
    left: 0px;
    top: 0px;
    z-index: 99;
    color: white;

    padding: 6rem var(--grid-gutter) var(--grid-gutter-quadruple) var(--grid-gutter);

    @media #{$breakpoint-horizontal} {
        @include transform(translate(-100vw, 0vh));
        padding: 6.5rem 2rem 6.5rem $header-width;
    }

    &:after {
        content: "";
        position: absolute;
        left: var(--grid-gutter);
        right: var(--grid-gutter);
        bottom: var(--grid-gutter-quadruple);

        height: var(--grid-gutter-double);
        background: linear-gradient(to top, #{$color-dark}, transparent);

        @media #{$breakpoint-horizontal} {
            height: var(--grid-gutter-triple);
            left: calc(6.5rem + var(--grid-gutter-double));
            right: 2rem;
            bottom: 6.5rem;
        }
    }

    &__inner {
        position: relative;
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        grid-template-rows: repeat(10, 1fr);
        gap: var(--grid-gutter-half);
        height: 100%;

        overflow-y: auto;
        padding-right: 16px; // scroll-bar

        @media #{$breakpoint-horizontal} {
            grid-template-columns: repeat(1, 1fr);
            grid-template-rows: repeat(5, 1fr);
            /* padding-left: $header-width; */
        }
        &__time {
            display: flex;
            grid-column: 1/2;
            grid-row: 1/2;
            @media #{$breakpoint-horizontal} {
                display: none;
            }
        }
        &__links {
            grid-column: 1/2;
            grid-row: 2/8;
            @media #{$breakpoint-horizontal} {
                grid-row: 1/1;
                @media screen and (min-height: 1000px) {
                    grid-row: 1/5;
                }
            }

            .c-header-nav-link-item:last-of-type {
                padding-bottom: var(--grid-gutter-double);

                @media #{$breakpoint-horizontal} {
                    padding-bottom: 0;
                }
            }
        }
        &__more {
            position: fixed;
            left: var(--grid-gutter);
            right: 0;
            bottom: var(--grid-gutter);
            padding: 0 var(--grid-gutter);

            /* for the animation when the user open the nav */
            @include transform(translate(0px, 50px));
            opacity: 0;

            grid-column: 1/2;
            grid-row: 10/11;

            @media #{$breakpoint-horizontal} {
                left: $header-width;
                grid-row: 5/6;
                align-self: end;
                padding: 0 var(--grid-gutter-double);
            }
        }
    }
}
</style>
